import {Injectable} from "@angular/core";
import { HttpClient } from "@angular/common/http";

import {map, mergeMap, Observable, of, tap} from "rxjs";

import {Vehicle} from "@commons/models/domain/Vehicle";
import {VehicleControlIndicator} from "@commons/models/domain/VehicleControlIndicator";

import {BaseHttpService} from "./base-http.service";
import {GetRouteDetailsListResponse} from "@shared/http-responses/get-route-details-list.response";
import { IBlockerInfo } from "@commons/models/remote.request/VehicleLock";
import { WebAccessService } from "@commons/services/web.access/web-access.service";

@Injectable({
  providedIn: 'root'
})
export class NewVehiclesHttpService extends BaseHttpService {
  public vehiclesBlockerInfos: Map<string, IBlockerInfo> = new Map();

  constructor(
    private httpClient: HttpClient,
    private webAccessService: WebAccessService,
  ) {
    super();
  }

  private convertVehicleInfosToMap(array: IBlockerInfo[]): Map<string, IBlockerInfo> {
    const map = new Map();
      array.forEach(item => {
          map.set(`${item.VehicleId}${item.OutName}`, item);
      });
      return map;
  }

  getRouteDetailsList(routeIds: number[]): Observable<GetRouteDetailsListResponse[]> {
    return this.httpClient.post<GetRouteDetailsListResponse[]>(this.getUrl('/GetRouteDetailsList'), {routeIds: routeIds})
      .pipe(map(x => (x as any).Result.Routes as GetRouteDetailsListResponse[]));
  }

  getById(vehicleId: number): Observable<Vehicle> {
    return this.httpClient.get<any>(this.getUrl('/GetVehicle/' + vehicleId))
      .pipe(map((v) => this.getVehicleFromGetByIdResponse(v)))
  }

  getVehiclesWithIndicators(loadOnlyVehicles: boolean): Observable<Vehicle[]> {
    return new Observable<Vehicle[]>(observer => {
      this.httpClient.get<any>(this.getUrl('/GetVehicleList'))
        .pipe(
          mergeMap((res) =>
            this.webAccessService.GetVehicleIgnitionBlockerDetailsList().pipe(
              tap((data) => {
                this.vehiclesBlockerInfos = this.convertVehicleInfosToMap(data.BlockerInfos);
              }),
              map(() => res),
            )
          ),
          map(result => {
            if (this.vehiclesBlockerInfos.size) {
              result.Result.Result.forEach((item: any) => {
                item.blockerParametrId = this.vehiclesBlockerInfos.get(`${item.Id}OUT1`)?.ParameterId;
                item.blockerParametrIdOut2 = this.vehiclesBlockerInfos.get(`${item.Id}OUT2`)?.ParameterId;
              });
            }
            return result.Result.Result as Vehicle[];
          })
        )
        .subscribe(vehicles => {
          vehicles.forEach(v => this.setVehicleLocation(v));
          if (loadOnlyVehicles) {
            observer.next(vehicles);
            observer.complete();
          } else {
            this.getAndSetIndicators(vehicles).subscribe(() => {
              observer.next(vehicles);
              observer.complete();
            });
          }
        });
    });
  }

  private getAndSetIndicators(vehicles: Vehicle[]): Observable<VehicleControlIndicator[]> {
    return this.httpClient.get<any>(this.getUrl('/GetVehicleParamList'))
      .pipe(
        tap(result => this.setVehiclesIndicators(vehicles, result.Result.Result as VehicleControlIndicator[])),
        map(result => result.Result)
      );
  }

  private getVehicleFromGetByIdResponse(response: any): Vehicle {
    const vehicle = response.Result.Vehicle as Vehicle;
    this.setVehicleLocation(vehicle);

    const indicators = response.Result.Indicators;
    if (!indicators || indicators.length == 0) {
      console.warn('No indicators for vehicle get by id=' + vehicle.Id);
      return vehicle;
    }

    vehicle.ControlIndicators = vehicle.ControlIndicators ?? [];
    for (let i = 0; i < indicators.length; i++) {
      const indicator = indicators[i];
      indicator.Group = this.findIndicatorGroup(indicator.Tp);
      vehicle.ControlIndicators.push(indicator);
    }

    return vehicle;
  }

  private setVehicleLocation(vehicle: Vehicle) {
    if (vehicle.LL) {
      const lat = parseFloat((vehicle.LL.Lt + '').replace(',', '.'));
      if (lat && lat !== 361) {
        vehicle.LL.Lt = lat;
      }

      const lng = parseFloat((vehicle.LL.Ln + '').replace(',', '.'));
      if (lng && lng !== 361) {
        vehicle.LL.Ln = lng;
      }
    }
  }

  private setVehiclesIndicators(vehicles: Vehicle[], indicators: VehicleControlIndicator[]) {
    for (let i = indicators.length - 1; i >= 0; i--) {
      const indicator = indicators[i];
      if (indicator == null)
        continue;

      // grupowanie
      indicator.Group = this.findIndicatorGroup(indicator.Tp);

      // przepisanie do pojazdu
      const vehicle = vehicles.find(x => x.Id == indicator.VI);
      if (vehicle) {
        vehicle.ControlIndicators = vehicle.ControlIndicators || [];
        if (indicator) {
          vehicle.ControlIndicators.push(indicator);
        }

        // status
        if (indicator.Tp === 1000 && indicator.LV) {
          vehicle.St = indicator.LV.V;
          if (indicator.LV.T) {
            vehicle.LastUpdated = indicator.LV.LT;
            vehicle.LastUpdatedFrom = 'INDICATORS_LOAD';
          }
        }

        // gelokacja
        if (indicator.Tp === 8 && indicator.LV) {
          const lat = parseFloat((indicator.LV.V.Lat + '').replace(',', '.'));
          if (lat && lat !== 361) {
            (vehicle as any).LL.Lt = lat;
          }
          const lng = parseFloat((indicator.LV.V.Lon + '').replace(',', '.'));
          if (lng && lng !== 361) {
            (vehicle as any).LL.Ln = lng;
          }
          // MCW-296
          if (indicator.LV.T) {
            vehicle.LastUpdated = indicator.LV.T;
            vehicle.LastUpdatedFrom = 'INDICATORS_GEO_LOAD';
          }
        }
      }

    }
  }

  private findIndicatorGroup(paramType: any): number | undefined {
    for (let i = 0; i <= this.groups.length - 1; i++) {
      const group = this.groups[i];
      if (paramType === group.Type) {
        return group.Group;
      }
    }
    return undefined;
  }

  private readonly groups = [
    {Type: 1, Name: 'Fuel', Desc: 'paliwo', Param: 'D1', Group: 8}
    , {Type: 2, Name: 'Speed', Desc: 'prędkość', Param: 'D1', Group: 12}
    , {Type: 5, Name: 'EngineSpeed', Desc: 'obroty silnika', Param: 'D1', Group: 7}
    , {Type: 6, Name: 'Temperature', Desc: 'temperatura', Param: 'D1', Group: 0}
    , {Type: 7, Name: 'Voltage', Desc: 'napięcie', Param: 'D1', Group: 6}
    , {Type: 8, Name: 'Location', Desc: 'lokalizacja', Param: '', Group: 1}
    , {Type: 9, Name: 'Binary', Desc: 'dwustanowy', Param: 'B', Group: 0}
    , {Type: 10, Name: 'Analog', Desc: 'analogowy', Param: 'D1', Group: 0}
    , {Type: 11, Name: 'Digital', Desc: 'cyfrowe', Param: 'L', Group: 0}
    , {Type: 12, Name: 'Pulse', Desc: 'impulsowy', Param: 'L', Group: 0}
    , {Type: 13, Name: 'Pressure', Desc: 'ciśnienie', Param: 'D1', Group: 0}
    , {Type: 14, Name: 'Humidity', Desc: 'wilgotność', Param: 'D1', Group: 0}
    , {Type: 15, Name: 'ElectricCurrent', Desc: 'natężenie prądu', Param: 'D1', Group: 0}
    , {Type: 16, Name: 'Ignition', Desc: 'zapłon (dwustanowy)', Param: 'B', Group: 0}
    , {Type: 17, Name: 'Distance', Desc: 'odległość (droga)', Param: 'D1', Group: 5}
    , {Type: 18, Name: 'Move', Desc: 'czujnik ruchu - wartość analogowa', Param: 'D1', Group: 0}
    , {Type: 19, Name: 'MoveBinary', Desc: 'czujnik ruchu - ruch/brak ruchu', Param: 'B', Group: 0}
    , {Type: 20, Name: 'Direction', Desc: 'kierunek', Param: 'D1', Group: 0}
    , {Type: 21, Name: 'VisibleSatellites', Desc: 'ilość widocznych satelitów', Param: 'L', Group: 14}
    , {Type: 22, Name: 'GSMLevel', Desc: 'poziom sygnału GSM', Param: 'L', Group: 15}
    , {Type: 23, Name: 'RFID', Desc: 'identyfikator karty/pastylki', Param: 'L', Group: 0}
    , {Type: 24, Name: 'Acceleration', Desc: 'przyspieszenie', Param: 'D1', Group: 10}
    , {Type: 26, Name: 'Crash', Desc: 'parametr wypadku', Param: 'B', Group: 0}
    , {Type: 27, Name: 'FuelUsage', Desc: 'zużycie paliwa w pojeździe', Param: 'D1', Group: 18}
    , {Type: 28, Name: 'WorkTime', Desc: 'czas pracy silnika', Param: 'D1', Group: 0}
    , {Type: 100, Name: 'GPSData', Desc: '', Param: '', Group: 14}
    , {
      Type: 101,
      Name: 'EcoDriving',
      Desc: 'eco driving - zbiorczy parametr (hamowania, zakręty itp.)',
      Param: '',
      Group: 0
    }
    , {
      Type: 1000,
      Name: 'VehicleStatus',
      Desc: 'czujnik wirtualny - status pojazdu dla MyCarWeb',
      Param: 'L',
      Group: 13,
      Values: [
        , {v: 1, name: 'Driving', desc: 'Jedzie'}
        , {v: 2, name: 'Parking', desc: 'Zaparkowany'}
        , {v: 3, name: 'NoGPS', desc: 'Brak GPS'}
        , {v: 4, name: 'NoGSM', desc: 'Brak GSM'}
        , {v: 5, name: 'Disconnected', desc: 'Odłączone'}
      ]
    }
    , {
      Type: 1001,
      Name: 'VehicleDirection',
      Desc: 'czujnik wirtualny - kierunek jazdy pojazdu dla MyCarWeb',
      Param: 'L',
      Group: 0
    }
    , {
      Type: 1002,
      Name: 'VirtualJoinedTemperatures ',
      Desc: 'czujnik wirtualny - zwraca połączone temperatury dla MyCarWeb',
      Param: 'S',
      Group: 17
    },
    {
      Type: 1003, Name: 'VirtualETollStatus', Desc: 'eToll', Param: 'S', Group: 19
    }
  ];

}
